import { useState, useCallback } from 'react';
import { ImageTransform, Annotation } from '@/utils/imageProcessing';

interface PinMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  aiPredictionIndex?: number;
}

export const usePhotoEditor = () => {
  const [transform, setTransform] = useState<ImageTransform>({
    rotation: 0,
    flipHorizontal: false,
    flipVertical: false,
    brightness: 0,
    contrast: 0,
  });

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [pins, setPins] = useState<PinMarker[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const rotate = useCallback((degrees: 90 | -90) => {
    setTransform((prev) => ({
      ...prev,
      rotation: (prev.rotation + degrees + 360) % 360,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const flipHorizontal = useCallback(() => {
    setTransform((prev) => ({
      ...prev,
      flipHorizontal: !prev.flipHorizontal,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const flipVertical = useCallback(() => {
    setTransform((prev) => ({
      ...prev,
      flipVertical: !prev.flipVertical,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const setBrightness = useCallback((value: number) => {
    setTransform((prev) => ({
      ...prev,
      brightness: value,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const setContrast = useCallback((value: number) => {
    setTransform((prev) => ({
      ...prev,
      contrast: value,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const autoEnhance = useCallback(() => {
    setTransform((prev) => ({
      ...prev,
      brightness: 10,
      contrast: 20,
    }));
    setHasUnsavedChanges(true);
  }, []);

  const resetTransform = useCallback(() => {
    setTransform({
      rotation: 0,
      flipHorizontal: false,
      flipVertical: false,
      brightness: 0,
      contrast: 0,
    });
    setHasUnsavedChanges(true);
  }, []);

  const addAnnotation = useCallback((annotation: Annotation) => {
    setAnnotations((prev) => [...prev, annotation]);
    setHasUnsavedChanges(true);
  }, []);

  const removeAnnotation = useCallback((id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    setHasUnsavedChanges(true);
  }, []);

  const updateAnnotation = useCallback((id: string, data: any) => {
    setAnnotations((prev) =>
      prev.map((a) => (a.id === id ? { ...a, data: { ...a.data, ...data } } : a))
    );
    setHasUnsavedChanges(true);
  }, []);

  const addPin = useCallback((pin: PinMarker) => {
    setPins((prev) => [...prev, pin]);
    setHasUnsavedChanges(true);
  }, []);

  const removePin = useCallback((id: string) => {
    setPins((prev) => prev.filter((p) => p.id !== id));
    setHasUnsavedChanges(true);
  }, []);

  const loadPhotoData = useCallback((photoData: {
    pins?: PinMarker[];
    transform?: ImageTransform;
    annotations?: Annotation[];
  }) => {
    setPins(photoData.pins || []);
    setTransform(photoData.transform || {
      rotation: 0,
      flipHorizontal: false,
      flipVertical: false,
      brightness: 0,
      contrast: 0,
    });
    setAnnotations(photoData.annotations || []);
    setHasUnsavedChanges(false);
  }, []);

  const restoreSnapshot = useCallback((snapshot: {
    annotations: Annotation[];
    pins: PinMarker[];
    transform: ImageTransform;
  }) => {
    setAnnotations(snapshot.annotations);
    setPins(snapshot.pins);
    setTransform(snapshot.transform);
    setHasUnsavedChanges(true);
  }, []);

  const clearUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  return {
    transform,
    annotations,
    pins,
    hasUnsavedChanges,
    // Transform methods
    rotate,
    flipHorizontal,
    flipVertical,
    setBrightness,
    setContrast,
    autoEnhance,
    resetTransform,
    // Annotation methods
    addAnnotation,
    removeAnnotation,
    updateAnnotation,
    // Pin methods
    addPin,
    removePin,
    setPins,
    // State management
    loadPhotoData,
    restoreSnapshot,
    clearUnsavedChanges,
  };
};
